<template>
  <div>
    <div>
      <main>
        <Onboarding
          v-if="!onboardingComplete"
          @finish="completeOnboarding"
          @skip="completeOnboarding"
        />
        <div v-if="onboardingComplete">
          <div class="bg-white lg:bg-gray-100">
            <div class="sm:mx-0 md:py-12">
              <div class="flex flex-col max-w-6xl mx-auto px-2">
                <div class="flex flex-col bg-white lg:shadow-lg px-0 md:px-2 lg:px-8 md:py-8
                rounded">
                  <div class="flex flex-col md:flex-row items-center mt-12 lg:mt-0 mb-3">
                    <h1
                      class="text-2xl md:text-3xl text-center md:text-left w-full
                      font-semibold lg:mt-0 transition duration-500 transform"
                      :class="{
                        'invisible opacity-0 translate-y-2': filteredSubmissions.length === 0
                      }"
                    >
                      Your personalized results
                    </h1>
                    <transition name="fade-in">
                      <span
                        v-if="!loading"
                        class="h-1 w-40 mb-5 bg-green-500 md:hidden"
                      ></span>
                    </transition>
                    <transition name="fade-in">
                      <button
                        v-if="!loading"
                        class="focus:outline-none focus:shadow-outline-green w-48
                          whitespace-no-wrap px-5 py-2 rounded hover:bg-gray-100 float-right"
                        @click.stop="showFilters = !showFilters"
                      >
                        <SVGIcon class="mr-1 text-green-500">
                          <g
                            :fill="hasFilters ? 'currentColor': 'none'"
                            stroke="currentColor"
                          >
                            <path stroke-width="50"
                                  d="M487.976 0H24.028C2.71 0-8.047 25.866
                  7.058 40.971L192 225.941V432c0 7.831 3.821 15.17 10.237 19.662l80
                  55.98C298.02 518.69 320 507.493 320 487.98V225.941l184.947-184.97C520.021
                  25.896 509.338 0 487.976 0z"></path>
                          </g>
                        </SVGIcon>
                        Preferences
                      </button>
                    </transition>
                  </div>

                  <transition name="fade-fast" mode="out-in">
                    <div
                      v-if="filteredSubmissions.length > 0 && !loading"
                      :key="'hasResults'"
                      class="flex flex-col"
                    >
                      <div class="flex flex-row items-center">
                        <div class="flex flex-col w-full">
                          <p class="text-center md:text-left text-gray-500 text-sm mb-5">
                            Created from {{ this.filteredSubmissions.length }} entries
                          </p>
                        </div>
                      </div>
                      <div class="flex flex-col lg:flex-row">
                        <div class="flex flex-row lg:flex-col w-full lg:w-1/4 p-3">
                          <div class="flex flex-col h-full w-full text-center lg:text-left
                          justify-center mb-3">
                            <h2 class="font-semibold text-4xl leading-tight">
                              {{ aprModel.average }}%
                            </h2>
                            <h3 class="font-semibold leading-tight text-sm mb-1">Average APR</h3>
                            <h4 class="text-gray-500 text-xs">
                              ({{ aprModel.min }}% - {{ aprModel.max }}%)
                            </h4>
                          </div>
                          <div class="flex flex-col h-full w-full text-center  lg:text-left
                          justify-center mb-3">
                            <h2 class="font-semibold text-4xl leading-tight">
                              ${{ loanAmountModel.average }}k
                            </h2>
                            <h3 class="font-semibold leading-tight text-sm mb-1">
                              Average Loan Amount
                            </h3>
                            <h4 class="text-gray-500 text-xs">
                              (${{ loanAmountModel.min }}k - ${{ loanAmountModel.max }}k)
                            </h4>
                          </div>
                          <div class="flex flex-col h-full w-full text-center lg:text-left
                          justify-center mb-3">
                            <h2 class="font-semibold text-4xl leading-tight">
                              {{ termModel.most }}yr
                            </h2>
                            <h3 class="font-semibold leading-tight text-sm mb-1">
                              Most common term
                            </h3>
                            <h4 class="text-gray-500 text-xs">
                              ({{ termModel.min }}yr - {{ termModel.max }}yr)
                            </h4>
                          </div>
                        </div>
                        <div class="flex flex-col w-full px-3">
                          <Histogram />
                          <h5 class="mt-5 mb-1 px-3 text-sm font-semibold text-gray-500">
                            Disclaimer
                          </h5>
                          <p class="mb-5 px-3 bg-gray-100 rounded text-sm md:bg-transparent">
                            These rates are averaged across a variety of parameters. Please use
                            this information directionally.
                            <br/>
                            Information presented is based on quotes reported by members. Share
                            your quotes to help out others.
                          </p>
                        </div>
                        <div class="flex flex-col w-full lg:w-1/3 py-5 justify-center">
                          <transition name="fade-fast">
                            <div
                              v-show="!loadingRecommendations">
                              <h5 class="font-semibold underline text-center lg:text-left mb-5">
                                Top Lenders
                              </h5>
                              <ul class="flex flex-row flex-wrap text-sm md:text-base px-3
                              md:px-0">
                                <li
                                  v-for="(item, i) in lenderModel"
                                  :key="`LenderModel-${i}`"
                                  class="mb-2 w-1/2 lg:w-full"
                                >
                                  {{ Number(item.value).toFixed(2) }}%
                                  <span class="mr-1 md:mr-3"></span> {{ item.name }}
                                </li>
                              </ul>
                            </div>
                          </transition>
                        </div>
                      </div>
                    </div>
                    <div
                      v-else
                      :key="'noResults'"
                      class="flex flex-col px-4 py-16 items-center justify-center"
                    >
                      <div v-if="!loading" :key="`no-results`">
                        <h1 class="text-center">
                          We couldn't find any results with those filters, try broadening your search.
                        </h1>
                        <p class="text-center mt-5 sm:mt-1">
                          <strong>Pro Tip:</strong> Select multiple credit score ranges to widen
                          the parameters or select multiple repayment plans.
                        </p>
                      </div>
                      <h1 v-if="loading" class="text-heading" :key="`loading`">Loading...</h1>
                      <img
                        src="/img/lady-under-tree.png"
                        alt="Lady Under Tree"
                        class="inline text-center mt-10 w-48 h-48"
                      />
                      <div v-if="!loading" class="mt-10">
                        <button
                          @click.stop="showFilters = true"
                          class="w-full flex items-center justify-center px-10 py-3
                      text-base leading-6 font-medium rounded-md text-white bg-green-700
                      hover:bg-green-500 focus:outline-none focus:shadow-outline-green
                      transition duration-150 ease-in-out md:py-4 md:text-lg"
                        >
                          Change Preferences
                        </button>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
            </div>

            <transition appear name="fade-in" mode="out-in">
              <div class="flex flex-col">
                <FilterDrawer
                  :active="showFilters"
                  :class="showFilters ? 'translate-x-0' : '-translate-x-full'"
                  @click.native.stop="() => false"
                  @openDrawer="showFilters = !showFilters"
                  @closeDrawer="showFilters = false"
                  @filterChange="handleFilterChange"
                />
              </div>
            </transition>
          </div>

          <transition appear name="fade-in">
            <RecommendationCards
              v-if="hasRecommendations"
            />
          </transition>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { getHistogramData, getSubmissions, getRecommendations } from '@/services/api';
import FilterDrawer from '@/components/filters/FilterDrawer.vue';
import RecommendationCards from '@/components/recommendations/RecommendationCards.vue';
import Histogram from '../components/histogram/Histogram.vue';
import Onboarding from '../components/onboarding/Onboarding.vue';

const localStorageExists = localStorage != null;
export default {
  components: {
    RecommendationCards,
    FilterDrawer,
    Onboarding,
    Histogram,
  },
  props: {
    success: {
      default: () => false,
    },
  },
  data() {
    return {
      loadingRecommendations: true,
      onboardingComplete: localStorageExists
        ? Boolean(localStorage.getItem('onboardingComplete')) : false,
      showSuccessMessage: !!this.success,
      showFilters: false,
      sortValue: 'apr',
      tooltips: {
        sourcedViaLeverEdge: 'A LeverEdge member received this quote through our negotiated deal. Sign-up at LeverEdge.org to check yours.',
        borrowed: 'A LeverEdge member took out this loan.',
        verified: 'This quote has been verified via a screenshot check.',
        unverified: 'This quote has not been verified.',
      },
      headers: [
        {
          label: 'Lender',
          value: 'lender.name',
          sortable: true,
        },
        {
          label: '',
          value: '',
          sortable: false,
        },
        {
          label: 'APR',
          value: 'apr',
          sortable: true,
          default: true,
          reverse: true,
        },
        {
          label: 'Credit Score',
          value: 'creditScore.score',
          sortable: true,
        },
        {
          label: 'Co-signer <br /> Credit Score',
          value: 'cosignerCreditScore.score',
          sortable: true,
        },
        {
          label: 'Loan Amount',
          value: 'loanAmount',
          sortable: true,
        },
        {
          label: 'Rate Type',
          value: 'rateType.type',
          sortable: true,
        },
        {
          label: 'Loan Term',
          value: 'loanTerm',
          sortable: true,
        },
        {
          label: 'Repayment Plan',
          value: 'repaymentPlan.plan',
          sortable: true,
        },
      ],
    };
  },
  computed: {
    selectedCreditScoreIds() {
      return this.selectedCreditScores.map((item) => item.id);
    },
    selectedCosignerCreditScoreIds() {
      return this.selectedCosignerCreditScores.map((item) => item.id);
    },
    selectedUniversityIds() {
      return this.selectedUniversities.map((item) => item.id);
    },
    selectedDegreeIds() {
      return this.selectedDegrees.map((item) => item.id);
    },
    selectedRateTypeIds() {
      return this.selectedRateTypes.map((item) => item.id);
    },
    selectedRepaymentPlanIds() {
      return this.selectedRepaymentPlans.map((item) => item.id);
    },
    selectedLenderIds() {
      return this.selectedLenders.map((item) => item.id);
    },
    allQuotes() {
      const quotes = [];
      this.submissions.forEach((submission) => {
        submission.quotes.forEach((quote) => {
          const {
            cosignerCreditScore,
            creditScore,
            degree,
            university,
          } = submission;

          quotes.push({
            ...quote,
            cosignerCreditScore,
            creditScore,
            degree,
            university,
          });
        });
      });
      return quotes;
    },
    filteredSubmissions() {
      let filtered = this.allQuotes || [];
      if (this.showCosignerCreditScores) {
        filtered = filtered
          .filter((submission) => submission.cosignerCreditScore.id !== 1);
      } else {
        filtered = filtered
          .filter((submission) => submission.cosignerCreditScore.id === 1);
      }

      if (this.selectedLoanTypes.length) {
        const lowerCaseTypes = this.selectedLoanTypes.map((type) => type.toLowerCase());
        filtered = filtered
          .filter((submission) => lowerCaseTypes.includes(submission.degree.type));
      }

      if (this.selectedCreditScoreIds.length) {
        filtered = filtered
          .filter((submission) => this.selectedCreditScoreIds
            .includes(submission.creditScore.id));
      }

      if (this.selectedCosignerCreditScoreIds.length) {
        filtered = filtered
          .filter((submission) => this.selectedCosignerCreditScoreIds
            .includes(submission.cosignerCreditScore.id));
      }

      if (this.selectedUniversityIds.length) {
        filtered = filtered
          .filter((submission) => this.selectedUniversityIds
            .includes(submission.university.id));
      }

      if (this.selectedDegreeIds.length) {
        filtered = filtered
          .filter((submission) => this.selectedDegreeIds
            .includes(submission.degree.id));
      }

      if (this.selectedRateTypeIds.length) {
        filtered = filtered
          .filter((submission) => this.selectedRateTypeIds
            .includes(submission.rateType.id));
      }

      if (this.selectedRepaymentPlanIds.length) {
        filtered = filtered
          .filter((submission) => this.selectedRepaymentPlanIds
            .includes(submission.repaymentPlan.id));
      }

      if (this.selectedLenderIds.length) {
        filtered = filtered
          .filter((submission) => this.selectedLenderIds
            .includes(submission.lender.id));
      }

      return filtered;
    },
    aprModel() {
      const aprs = this.filteredSubmissions
        .map((submission) => submission.apr)
        .sort((a, b) => a - b);
      const total = aprs
        .reduce((accumulator, currentValue) => Number(accumulator) + Number(currentValue));
      return {
        min: Number(aprs[0]).toFixed(2),
        max: Number(aprs[aprs.length - 1]).toFixed(2),
        average: Number(total / aprs.length).toFixed(2),
      };
    },
    loanAmountModel() {
      const amounts = this.filteredSubmissions
        .map((submission) => Math.floor(submission.loanAmount / 1000))
        .sort((a, b) => a - b);
      const total = amounts
        .reduce((accumulator, currentValue) => Number(accumulator) + Number(currentValue));
      return {
        min: Number(amounts[0]),
        max: Number(amounts[amounts.length - 1]),
        average: Math.floor(Number(total / amounts.length)),
      };
    },
    termModel() {
      const terms = this.filteredSubmissions
        .map((submission) => submission.loanTerm)
        .sort((a, b) => a - b);
      const totals = {};
      terms.forEach((term) => {
        if (totals[term]) {
          totals[term] += 1;
        } else {
          totals[term] = 1;
        }
      });
      const most = Object.keys(totals).reduce(
        (accumulator, currentValue) => {
          if (totals[accumulator] > totals[currentValue]) {
            return Number(accumulator);
          }
          return Number(currentValue);
        },
      );
      return {
        min: Number(terms[0]),
        max: Number(terms[terms.length - 1]),
        most,
      };
    },
    lenderModel() {
      const recommendation = this.recommendationData;
      const model = Object.keys(recommendation.lenderRecommendation).map((item) => {
        const { name } = this.lenders.find(
          (lender) => Number(lender.id) === Number(item),
        );
        const value = recommendation.lenderRecommendation[item];
        return {
          name,
          value,
        };
      });
      return model.sort((a, b) => a.value - b.value);
    },
    hasRecommendations() {
      return Object.values(this.recommendationData)
        .find((value) => value !== false) != null;
    },
    quotePayload() {
      return {
        hasCosigner: this.showCosignerCreditScores,
        creditScores: this.selectedCreditScores,
        cosignerCreditScores: this.selectedCosignerCreditScores,
        universities: this.selectedUniversities,
        degreeTypes: this.selectedLoanTypes.map((item) => item.toLowerCase()),
        rateTypes: this.selectedRateTypes,
        repaymentPlans: this.selectedRepaymentPlans,
        lenders: this.selectedLenders,
      };
    },
    hasFilters() {
      const filters = [
        this.selectedLoanTypes.length,
        this.selectedCreditScores.length,
        this.selectedCosignerCreditScores.length,
        this.selectedUniversities.length,
        this.selectedDegrees.length,
        this.selectedRateTypes.length,
        this.selectedRepaymentPlans.length,
        this.selectedLenders.length,
      ];
      return this.showCosignerCreditScores
        || filters.find((filter) => filter !== 0) != null;
    },
    ...mapGetters('base', [
      'reversedCreditScores',
      'reversedCosignerCreditScores',
    ]),
    ...mapState('base', [
      'loading',
      'showCosignerCreditScores',
      'universities',
      'creditScores',
      'cosignerCreditScores',
      'degrees',
      'rateTypes',
      'repaymentPlans',
      'lenders',
      'selectedLoanTypes',
      'selectedCreditScores',
      'selectedCosignerCreditScores',
      'selectedUniversities',
      'selectedDegrees',
      'selectedRateTypes',
      'selectedRepaymentPlans',
      'selectedLenders',
    ]),
    ...mapState('submission', [
      'submissions',
      'recommendationData',
    ]),
  },
  methods: {
    ...mapActions('base', [
      'setAllOptions',
      'setBins',
      'setSelectedValue',
      'toggleCosigners',
      'setFooterDisplay',
    ]),
    ...mapActions('submission', [
      'setAllSubmissions',
      'setRecommendationData',
    ]),
    formatCurrency(value) {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });

      return formatter.format(value);
    },
    parseCreditScore(creditScore) {
      if (creditScore[0] === 'I') {
        return '-';
      }
      return creditScore;
    },
    handleFilterChange() {
      const { submissionTable } = this.$refs;
      if (submissionTable) {
        submissionTable.loadPage(1);
      }
      getHistogramData(this.quotePayload)
        .then((response) => {
          if (!response) {
            return false;
          }
          this.setBins(response.data);
          if (localStorageExists) {
            localStorage.setItem('bins', JSON.stringify(response.data));
          }
          return this.fetchRecommendations();
        });
    },
    completeOnboarding() {
      if (localStorageExists) {
        localStorage.setItem('onboardingComplete', 'true');
      }
      this.onboardingComplete = true;
    },
    fetchRecommendations() {
      getRecommendations(this.quotePayload)
        .then((response) => {
          if (response) {
            this.setRecommendationData(response.data);
            this.loadingRecommendations = false;
          }
        });
    },
  },
  watch: {
    onboardingComplete(value) {
      if (value) {
        this.setFooterDisplay(true);
      }
    },
  },
  mounted() {
    if (this.onboardingComplete) {
      this.setFooterDisplay(true);
    } else {
      this.setFooterDisplay(false);
    }
    if (this.success) {
      getSubmissions()
        .then((response) => {
          if (response) {
            this.setAllSubmissions(response);
            localStorage.setItem('submissions', JSON.stringify(response.data.data));
          }
        });
    }
    if (!this.loading) {
      this.fetchRecommendations();
    } else {
      this.$root.$on('dataLoaded', () => {
        this.fetchRecommendations();
      });
    }
  },
  beforeDestroy() {
    this.$root.$off('dataLoaded');
  },
};
</script>
